import { useRef, useState } from 'react';
import { ApiError, type OwnerCertification } from '../api';
import { stateName, verifyMessage, type VerifyNotice } from '../format';

/** A failed verify request as the notice the owner reads (U6's refusals). */
function noticeFor(error: unknown): VerifyNotice {
  if (!(error instanceof ApiError)) return { kind: 'unreachable' };
  // Only the verify switch's own answer is a pause; any other 503 is the Worker not answering.
  if (error.status === 503) return error.body.error === 'verification_paused' ? { kind: 'paused' } : { kind: 'unreachable' };
  if (error.status === 429) return { kind: 'rate_limited', retryAfter: typeof error.body.retryAfter === 'string' ? error.body.retryAfter : null };
  if (error.status === 400) return { kind: 'refused', error: typeof error.body.error === 'string' ? error.body.error : 'no_match' };
  return { kind: 'unreachable' };
}

/**
 * Verify for one Texas or Florida certificate (F2), and the live region that says how it went.
 * The region is always there, so a screen reader hears each change. A press while a check is
 * running does nothing: the ref answers before React has redrawn the disabled button.
 */
export function VerifyButton({ certification, enabled, onVerify }: {
  certification: OwnerCertification;
  /** The verify switch (R16); off hides the button and says verification is paused. */
  enabled: boolean;
  onVerify(id: string): Promise<void>;
}) {
  const running = useRef(false);
  const [notice, setNotice] = useState<VerifyNotice | null>(enabled ? null : { kind: 'paused' });
  const paused = !enabled || notice?.kind === 'paused';
  const checking = notice?.kind === 'checking';
  const verified = certification.verification?.status === 'verified';

  const verify = async () => {
    if (running.current) return;
    running.current = true;
    setNotice({ kind: 'checking' });
    try {
      await onVerify(certification.id);
      setNotice(null);
    } catch (error) {
      setNotice(noticeFor(error));
    } finally {
      running.current = false;
    }
  };

  const message = verified && !notice ? null : verifyMessage(certification.verification, paused ? { kind: 'paused' } : notice);
  return <div className="verify">
    {!paused && !verified && <button type="button" className="secondary verify-button" disabled={checking} aria-busy={checking} onClick={() => void verify()}>
      Verify with {stateName(certification.state)}
    </button>}
    <p className="verify-status" role="status" aria-live="polite">{message}</p>
  </div>;
}
